import React, { useEffect, useState, useRef } from "react";
import styles from "./drop_menu.module.css";
import { get, post } from "util/requests";
import LoadingButton from "@mui/lab/LoadingButton";
import { Dialog, TextField } from "@mui/material";
import { DataIcon, DataIconBlue } from "ui/icons";
import { useSelector } from "react-redux";
import SignaturePassword from "ui/crfs/signature_password";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import router from "routes";
import strings from "strings";
import Tooltip from "@mui/material/Tooltip";
import { OpenQueryIcon, RespondedQueryIcon, ResolvedQueryIcon } from "ui/icons";
import { arrayHaveCommonItems } from "util/helpers";

export default function DropMenu(props) {
  const {
    form_name,
    crf_id,
    crf_data,
    crf_status,
    crf_sign_status,
    subject_id,
    updateCrfStatus,
    updateCrfSignStatus,
    showDeleteFormOption,
    deleteForm,
    multi_entry_crf_id,
    all_data_complete,
    builder_link,
    batchUpdateFieldFlag,
    crfFlags,
    updateNonCrfFlags,
    is_consent_form = false,
    is_deviation_form = false,
    data,
    setQueryModalData,
  } = props;
  const permissions = useSelector((state) => state.user.userData.permissions);
  const study_id = useSelector((state) => state.study.active_study_id);

  const [showingInvestigatorSignModal, setShowingInvestigatorSignModal] = useState(false);
  const [showingDeleteModal, setShowingDeleteModal] = useState(false);
  const [showingDataEntryCompleteModal, setShowingDataEntryCompleteModal] = useState(false);
  const [showingRemoveDataEntryCompleteModal, setShowingRemoveDataEntryCompleteModal] = useState(false);
  const [showingRemoveDataMonitoredModal, setShowingRemoveDataMonitoredModal] = useState(false);
  const [showingDataMonitoredModal, setShowingDataMonitoredModal] = useState(false);
  const [modalText, setModalText] = useState("");
  const [password, setPassword] = useState("");
  const ref = useRef(null);

  const [showingMenu, setShowingMenu] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const dropMenuRef = useRef(null);

  const onKeyDown = (key) => {
    if (key.keyCode === 13) {
    }
  };

  const handleDocumentClick = (event) => {
    if (event.target && dropMenuRef && dropMenuRef.current && !dropMenuRef.current.contains(event.target)) {
      setShowingMenu(false);
    }
  };

  useEffect(() => {
    setPassword("");
  }, [crf_data && crf_data.crf_id]);

  useEffect(() => {
    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    setModalText("");
  }, [showingRemoveDataMonitoredModal, showingRemoveDataEntryCompleteModal]);

  let status = null;

  const setDataEntryComplete = () => {
    let url = `/crf_status/${subject_id}/${crf_id}`;
    if (multi_entry_crf_id) {
      url = `/crf_status/${subject_id}/${crf_id}/${multi_entry_crf_id}`;
    }
    post(url, {
      status: "data_complete",
      study_id,
    }).then(() => setShowingDataEntryCompleteModal(false));
    updateCrfStatus(crf_id, "data_complete", multi_entry_crf_id);
  };

  const removeDataEntryComplete = () => {
    let url = `/crf_status/${subject_id}/${crf_id}`;
    if (multi_entry_crf_id) {
      url = `/crf_status/${subject_id}/${crf_id}/${multi_entry_crf_id}`;
    }
    post(url, {
      status: "data",
      reason: modalText,
    }).then(() => setShowingRemoveDataEntryCompleteModal(false));
    updateCrfStatus(crf_id, "remove_data_complete", multi_entry_crf_id);
    setModalText("");
  };

  const signCrf = (status, password) => {
    let url = `/sign_crf/${subject_id}/${crf_id}`;
    if (multi_entry_crf_id) {
      url = `/sign_crf/${subject_id}/${crf_id}/${multi_entry_crf_id}`;
    }
    return new Promise((resolve) => {
      post(url, { status, password })
        .then(() => {
          updateCrfSignStatus(crf_id, status);
          resolve({ success: true });
        })
        .catch(() => resolve({ success: false }));
    });
  };

  const unsignCrf = (status, reason) => {
    let url = `/unsign_crf/${subject_id}/${crf_id}`;
    if (multi_entry_crf_id) {
      url = `/unsign_crf/${subject_id}/${crf_id}/${multi_entry_crf_id}`;
    }
    return new Promise((resolve) => {
      post(url, { status, reason })
        .then(() => {
          updateCrfSignStatus(crf_id, status, true);
          resolve({ success: true });
        })
        .catch(() => resolve({ success: false }));
    });
  };

  if (!crf_status && !crf_sign_status) {
    return null;
  }

  const menu_show_monitored =
    // !is_linked_crf &&
    permissions.includes("set_monitored_flag") && !(crf_sign_status && crf_sign_status.includes("monitored"));
  const menu_show_remove_monitored =
    permissions.includes("set_monitored_flag") && crf_sign_status && crf_sign_status.includes("monitored");
  const menu_show_site_signoff =
    permissions.includes("set_pi_signed_flag") &&
    crfFlags?.includes("all_data") &&
    (!crfFlags?.includes("all_site_signoff") || crfFlags?.includes("resite_signoff"));

  const menu_show_remove_site_signoff =
    crfFlags?.includes("all_site_signoff") && permissions.includes("set_pi_signed_flag");

  const menu_show_qa = permissions.includes("set_qc_flag");

  const menu_show_crf_builder = permissions.includes("crf_builder") && !is_consent_form && !is_deviation_form;
  const menu_show_query = permissions.includes("start_query");
  const menu_items_exist = () =>
    menu_show_crf_builder ||
    menu_show_qa ||
    menu_show_monitored ||
    menu_show_remove_monitored ||
    menu_show_site_signoff ||
    showDeleteFormOption ||
    menu_show_query;

  const get_string = (string) => {
    let string_context = "crf";
    if (is_consent_form) {
      string_context = "consent";
    } else if (is_deviation_form) {
      string_context = "deviation";
    }
    let string_array = {
      set_monitored: {
        consent: "Set monitored flag on consent form",
        crf: "Set monitored flag on all fields",
        deviation: "Set monitored flag on deviation form",
      },
      remove_monitored: {
        consent: "Remove monitored flag on consent form",
        crf: "Remove monitored flag on all fields",
        deviation: "Remove monitored flag on deviation form",
      },
      set_qc: {
        consent: "Set QC flag on consent form",
        crf: "Set QC flag on all fields",
        deviation: "Set QC flag on deviation form",
      },
      remove_qc: {
        consent: "Remove QC flag on consent form",
        crf: "Remove QC flag on all fields",
        deviation: "Remove QC flag on deviation form",
      },
    };
    return string_array[string][string_context];
  };

  const get_flag_icons = () => {
    let flags = crfFlags || [];
    let ret = [];
    let show_query = arrayHaveCommonItems(permissions, [
      "start_query",
      "resolve_query",
      "respond_query",
      "view_query_status",
    ]);

    let open_query = () => {
      setQueryModalData({
        ...data.query,
        showing: true,
        slug: "consent",
        crf_id: data.consent_id,
      });
    };

    if (show_query && data?.query?.status === "open") {
      ret.push(
        <div className={styles.clickable}>
          <Tooltip title={strings.query_open} key={1} onClick={open_query}>
            <OpenQueryIcon fontSize="16px" />
          </Tooltip>
        </div>
      );
    }

    if (show_query && data?.query?.status === "responded") {
      ret.push(
        <div className={styles.clickable}>
          <Tooltip title={strings.query_responded} key={1} onClick={open_query}>
            <RespondedQueryIcon fontSize="16px" />
          </Tooltip>
        </div>
      );
    }

    if (show_query && data?.query?.status === "resolved") {
      ret.push(
        <div className={styles.clickable}>
          <Tooltip title={strings.query_resolved} key={1} onClick={open_query}>
            <ResolvedQueryIcon fontSize="16px" />
          </Tooltip>
        </div>
      );
    }

    if (permissions.includes("set_pi_signed_flag") || permissions.includes("view_pi_signed_flag")) {
      if (flags?.includes("resite_signoff")) {
        ret.push(
          <div className={styles.re_icon}>
            <Tooltip title={strings.re_site_signoff} key={1}>
              S
            </Tooltip>
          </div>
        );
      } else if (flags?.includes("all_site_signoff")) {
        ret.push(
          <div className={styles.all_icon}>
            <Tooltip title={strings.crf_site_signoff} key={1}>
              S
            </Tooltip>
          </div>
        );
      }
    }
    if (permissions.includes("set_monitored_flag") || permissions.includes("view_monitored_flag")) {
      if (flags?.includes("remonitored")) {
        ret.push(
          <div className={styles.re_icon}>
            <Tooltip title={strings.re_monitor} key={1}>
              M
            </Tooltip>
          </div>
        );
      } else if (flags?.includes("all_monitored")) {
        ret.push(
          <div className={styles.all_icon}>
            <Tooltip title={strings.all_fields_monitored} key={1}>
              M
            </Tooltip>
          </div>
        );
      } else if (flags?.includes("monitored")) {
        ret.push(
          <div className={styles.partial_icon}>
            <Tooltip title={strings.partial_monitored_flag} key={1}>
              M
            </Tooltip>
          </div>
        );
      }
    }

    if (permissions.includes("set_qc_flag") || permissions.includes("view_qc_flag")) {
      if (flags?.includes("reqa")) {
        ret.push(
          <div className={styles.re_icon}>
            <Tooltip title={strings.re_qa} key={1}>
              Q
            </Tooltip>
          </div>
        );
      } else if (flags?.includes("qa") && !flags?.includes("all_qa")) {
        ret.push(
          <div className={styles.partial_icon}>
            <Tooltip title={strings.partial_qa_flag} key={1}>
              Q
            </Tooltip>
          </div>
        );
      } else if (flags?.includes("all_qa")) {
        ret.push(
          <div className={styles.all_icon}>
            <Tooltip title={strings.all_qa_flag} key={1}>
              Q
            </Tooltip>
          </div>
        );
      }
    }

    if (flags?.includes("all_data")) {
      ret.push(
        <div className={styles.data_icon}>
          <Tooltip title={strings.all_data_flag} key={1}>
            <DataIconBlue fontSize={"16px"} />
          </Tooltip>
        </div>
      );
    } else if (flags?.includes("data")) {
      ret.push(
        <div className={styles.data_icon}>
          <Tooltip title={strings.partial_data_flag} key={1}>
            <DataIcon fontSize={"16px"} />
          </Tooltip>
        </div>
      );
    }

    return ret;
  };

  return (
    <div className={styles.qa_wrapper}>
      <div className={styles.tag_wrapper}>
        {get_flag_icons()}

        <div className={styles.hover_menu_wrapper}>
          {menu_items_exist() ? (
            <div className={styles.ellipsis_button} onMouseEnter={() => setShowingMenu(true)}>
              <MoreVertIcon onClick={() => setShowingMenu(true)} />
            </div>
          ) : null}
          {showingMenu ? (
            <div className={styles.hover_menu_content} ref={dropMenuRef}>
              {menu_show_monitored && !crfFlags?.includes("all_monitored") ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    if (is_consent_form || is_deviation_form) {
                      updateNonCrfFlags("all_monitored").then(() => setShowingMenu(false));
                    } else {
                      setShowingMenu(false);
                      setShowingDataMonitoredModal(true);
                    }
                  }}
                >
                  {get_string("set_monitored")}
                </div>
              ) : null}
              {menu_show_monitored && crfFlags?.includes("all_monitored") ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    if (is_consent_form || is_deviation_form) {
                      updateNonCrfFlags("all_monitored", false).then(() => setShowingMenu(false));
                    } else {
                      setShowingMenu(false);
                      setShowingRemoveDataMonitoredModal(true);
                    }
                  }}
                >
                  {get_string("remove_monitored")}
                </div>
              ) : null}

              {menu_show_qa && !crfFlags?.includes("all_qa") ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    if (is_consent_form || is_deviation_form) {
                      updateNonCrfFlags("all_qa").then(() => setShowingMenu(false));
                    } else {
                      setShowingMenu(false);
                      batchUpdateFieldFlag("qa", true);
                    }
                  }}
                >
                  {get_string("set_qc")}
                </div>
              ) : null}

              {menu_show_qa && crfFlags?.includes("all_qa") ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    if (is_consent_form || is_deviation_form) {
                      updateNonCrfFlags("all_qa", false).then(() => setShowingMenu(false));
                    } else {
                      setShowingMenu(false);
                      batchUpdateFieldFlag("qa", false);
                    }
                  }}
                >
                  {get_string("remove_qc")}
                </div>
              ) : null}
              {(is_consent_form || is_deviation_form) && menu_show_query ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    if (is_consent_form || is_deviation_form) {
                      setQueryModalData({
                        ...data.query,
                        showing: true,
                        slug: "consent",
                        crf_id: data.consent_id,
                      });
                    } else {
                    }
                  }}
                >
                  Query
                </div>
              ) : null}

              {menu_show_site_signoff ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    setShowingMenu(false);
                    setShowingInvestigatorSignModal(true);
                  }}
                >
                  Site Investigator Signature on all Fields
                </div>
              ) : null}
              {menu_show_remove_site_signoff ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    setShowingMenu(false);
                    batchUpdateFieldFlag("site_signoff", false);
                  }}
                >
                  Remove Site Investigator Signature
                </div>
              ) : null}

              {showDeleteFormOption ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    setShowingMenu(false);
                    setShowingDeleteModal(true);
                  }}
                >
                  Delete Form
                </div>
              ) : null}
              {menu_show_crf_builder ? (
                <div
                  className={styles.menu_button}
                  onClick={() => {
                    router.navigate(builder_link);
                  }}
                >
                  CRF Builder
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {false &&
      permissions.includes("set_monitored_flag") &&
      !(crf_sign_status && crf_sign_status.includes("monitored")) ? (
        <div>
          <LoadingButton
            disabled={false}
            variant="contained"
            loading={false}
            onClick={() => setShowingDataMonitoredModal(true)}
            sx={{
              fontSize: 14,
              marginLeft: "6px",
              padding: "2px",
              paddingLeft: "8px",
              paddingRight: "8px",
            }}
          >
            SET DATA TO MONITORED
          </LoadingButton>
        </div>
      ) : null}

      {false &&
      crf_status === "data_complete" &&
      !(crf_sign_status && crf_sign_status.includes("site_review")) &&
      permissions.includes("set_pi_signed_flag") ? (
        <div>
          <LoadingButton
            disabled={false}
            variant="contained"
            loading={false}
            onClick={() => setShowingInvestigatorSignModal(true)}
            sx={{
              fontSize: 14,
              marginLeft: "6px",
              padding: "2px",
              paddingLeft: "8px",
              paddingRight: "8px",
            }}
          >
            INVESTIGATOR SIGN
          </LoadingButton>
        </div>
      ) : null}

      <Dialog open={showingDeleteModal} onClose={() => setShowingDeleteModal(false)}>
        <div className={styles.modal_wrapper}>
          <div className={styles.modal_title}>Are you sure you want to delete this form?</div>
          <div className={styles.modal_button_wrapper}>
            <LoadingButton
              disabled={false}
              variant="contained"
              loading={false}
              onClick={() => {
                setSpinning(true);
                deleteForm().then(() => {
                  setShowingDeleteModal(false);
                  router.navigate(-1);
                });
              }}
            >
              YES
            </LoadingButton>
            <LoadingButton
              disabled={false}
              variant="outlined"
              loading={false}
              onClick={() => setShowingDeleteModal(false)}
            >
              NO
            </LoadingButton>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={showingInvestigatorSignModal}
        onClose={() => {
          setShowingInvestigatorSignModal(false);
          setPassword("");
        }}
      >
        <div className={styles.modal_wrapper}>
          <div className={styles.modal_title}>Electronic Signature Entry</div>
          <div className={styles.sign_for_label}>Enter your password to sign for:</div>
          <div className={styles.form_name_text}>{form_name}</div>
          <div className={styles.password_wrapper}>
            <SignaturePassword
              submit_text={"SUBMIT"}
              onSubmit={(password) => {
                //this returns to the signature form to set button state
                return new Promise((resolve) => {
                  batchUpdateFieldFlag("site_signoff", true, { password }).then(({ success }) => {
                    if (success) {
                      setShowingInvestigatorSignModal(false);
                      setPassword("");
                    }
                    resolve(success);
                  });
                  // signCrf("site_review", password).then(({ success }) => {
                  //   if (success) {
                  //     setShowingInvestigatorSignModal(false);
                  //   }
                  //   resolve(success);
                  // });

                  // handleRandomize({
                  //   password,
                  //   modal_confirmation: confirmation,
                  // }).then(({ success }) => {
                  //   resolve(success);
                  // });
                });
              }}
              disabled={false}
              password={password}
              setPassword={setPassword}
              showCancel
              cancel={() => setShowingInvestigatorSignModal(false)}
            />
          </div>
          {/*} <LoadingButton
            disabled={false}
            variant="contained"
            loading={false}
            onClick={() =>
              signCrf("site_review").then(() => setShowingModal(false))
            }
          >
            SIGN
          </LoadingButton>*/}
        </div>
      </Dialog>
      <Dialog open={showingDataEntryCompleteModal} onClose={() => setShowingDataEntryCompleteModal(false)}>
        <div className={styles.modal_wrapper}>
          <div className={styles.modal_title}>Set Data Entry to Complete</div>
          <div className={styles.modal_body}>
            <div className={styles.margin_bottom_10}>This will flag the data as complete for this CRF.</div>
            {!all_data_complete ? (
              <React.Fragment>
                <div className={styles.margin_bottom_10}>
                  <span className={styles.bold}>
                    The data entry DOES NOT appear to be complete. Please only proceed if you are sure the data entry is
                    complete.
                  </span>
                </div>

                <div></div>
              </React.Fragment>
            ) : (
              <div className={styles.italics}>(All data appears complete)</div>
            )}
          </div>
          <div className={styles.modal_button_wrapper}>
            <LoadingButton disabled={false} variant="contained" loading={false} onClick={setDataEntryComplete}>
              Proceed
            </LoadingButton>
            <LoadingButton
              disabled={false}
              variant="outlined"
              loading={false}
              onClick={() => setShowingDataEntryCompleteModal(false)}
            >
              Cancel
            </LoadingButton>
          </div>
        </div>
      </Dialog>
      <Dialog open={showingRemoveDataEntryCompleteModal} onClose={() => setShowingRemoveDataEntryCompleteModal(false)}>
        <div className={styles.modal_wrapper}>
          <div className={styles.modal_title}>Remove Data Complete Flag on this CRF</div>
          <div className={styles.modal_body}>
            <div className={styles.margin_bottom_10}>This will remove the Data Complete flag on this CRF.</div>
            <div className={styles.margin_bottom_10}>Please enter a reason why this is being done.</div>
            <TextField
              placeholder=""
              value={modalText}
              onChange={(e) => setModalText(e.target.value)}
              autoComplete="off"
              sx={{
                "& .MuiInputBase-input": {
                  padding: "5px",
                  paddingLeft: "10px",
                  textAlign: "left",
                },
              }}
              fullWidth
              multiline
            />
          </div>
          <div className={styles.modal_button_wrapper}>
            <LoadingButton
              disabled={modalText.length < 4}
              variant="contained"
              loading={false}
              onClick={removeDataEntryComplete}
            >
              SUBMIT
            </LoadingButton>
            <LoadingButton
              disabled={false}
              variant="outlined"
              loading={false}
              onClick={() => {
                setShowingRemoveDataEntryCompleteModal(false);
                setModalText("");
              }}
            >
              CANCEL
            </LoadingButton>
          </div>
        </div>
      </Dialog>
      <Dialog open={showingDataMonitoredModal} onClose={() => setShowingDataMonitoredModal(false)}>
        <div className={styles.modal_wrapper}>
          <div className={styles.modal_title}>Set Monitored Flags</div>
          <div className={styles.modal_body}>
            <div>
              This will set the monitored flag on <strong>all fields with data</strong> for this CRF.
            </div>
            <div>Would you like to proceed?</div>
          </div>
          <div className={styles.modal_button_wrapper}>
            <LoadingButton
              disabled={false}
              variant="contained"
              loading={false}
              onClick={() => {
                batchUpdateFieldFlag("monitored", true).then(setShowingDataMonitoredModal(false));
                // signCrf("monitored").then(() => setShowingDataMonitoredModal(false));
              }}
            >
              YES
            </LoadingButton>
            <LoadingButton
              disabled={false}
              variant="outlined"
              loading={false}
              onClick={() => setShowingDataMonitoredModal(false)}
            >
              NO
            </LoadingButton>
          </div>
        </div>
      </Dialog>
      <Dialog open={showingRemoveDataMonitoredModal} onClose={() => setShowingRemoveDataMonitoredModal(false)}>
        <div className={styles.modal_wrapper}>
          <div className={styles.modal_title}>Remove Monitored Flags on this CRF</div>
          <div className={styles.modal_body}>
            <div className={styles.margin_bottom_10}>This will remove all Monitored flags on this CRF.</div>
          </div>
          <div className={styles.modal_button_wrapper}>
            <LoadingButton
              variant="contained"
              loading={false}
              onClick={() => {
                batchUpdateFieldFlag("monitored", false, { reason: modalText }).then(
                  setShowingRemoveDataMonitoredModal(false)
                );
                // unsignCrf("monitored", modalText).then(() => setShowingRemoveDataMonitoredModal(false));
              }}
            >
              Continue
            </LoadingButton>
            <LoadingButton
              disabled={false}
              variant="outlined"
              loading={false}
              onClick={() => {
                setShowingRemoveDataMonitoredModal(false);
                setModalText("");
              }}
            >
              CANCEL
            </LoadingButton>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
